import { useState } from 'react'
import {
  ActionIcon, Alert, Badge, Button, Group, Modal, NumberInput,
  Stack, Table, Text, TextInput,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import {
  IconAlertTriangle, IconHeartbeat, IconPlayerPause, IconPlayerPlay,
  IconPlus, IconTrash,
} from '@tabler/icons-react'
import { DataCard, EmptyState } from '@/components/ui'
import { useCan } from '@/store/auth'
import { useHeartbeatSources, useHeartbeatSourceMutations } from '@/hooks/useAlerts'
import { formatTimeAgo } from '@/utils/formatTimeAgo'
import type { HeartbeatSource } from '@/api/alerts'

/**
 * Registro de fontes vigiadas por heartbeat — TASK-10040.
 *
 * Sem esta tela a vigilância da TASK-10038 é no-op em produção:
 * `event_source_heartbeats` nasce vazia de propósito (semear `wazuh` na migration
 * hardcodaria a primeira fonte, e o requisito é genérico), então o vigia roda a cada
 * 60 s e não tem o que vigiar.
 *
 * A coluna que manda na tela é **Sinal**, não o cadastro: fonte atrasada é incidente,
 * e quem abre esta tela abre para descobrir isso — não para ler `window_seconds`.
 */

// O vigia tica a cada 60 s: janela menor alertaria em todo tick, por construção.
// Espelha JANELA_MINIMA_SEGUNDOS/JANELA_MAXIMA_SEGUNDOS do backend, que tem teste de
// paridade contra os limites do Pydantic.
const JANELA_MINIMA = 60
const JANELA_MAXIMA = 30 * 24 * 3600

/** Duração legível: `formatTimeAgo` responde "quando", isto responde "de quanto em quanto". */
export function formatJanela(segundos: number): string {
  if (segundos < 60) return `${segundos} s`
  if (segundos < 3600) return `${Math.round(segundos / 60)} min`
  if (segundos < 86400) {
    const h = segundos / 3600
    return `${Number.isInteger(h) ? h : h.toFixed(1)} h`
  }
  const d = segundos / 86400
  return `${Number.isInteger(d) ? d : d.toFixed(1)} d`
}

export function HeartbeatSourcesAdmin() {
  const can = useCan()
  if (!can('alerts', 'manage')) {
    return (
      <Alert color="yellow" title="Acesso restrito" icon={<IconAlertTriangle size={18} />}>
        Você não possui permissão para gerenciar fontes de heartbeat.
      </Alert>
    )
  }
  return <HeartbeatSourcesAdminInner />
}

/** Uma célula, duas camadas: o veredito e a evidência dele. */
function CelulaSinal({ f }: { f: HeartbeatSource }) {
  const inativa = !f.is_active
  // `red.8` e nao o `red.6` do DS §2.3: com o badge de 11px/700 do tema (DS §6.6), o
  // branco sobre red.6 mede 3.84:1 — abaixo de 4.5:1. Mesma semantica de perigo, tom
  // que passa. Fica registrado como divida do DS, nao como gosto meu.
  const cor = inativa ? 'gray' : f.atrasada ? 'red.8' : f.nunca_sinalizou ? 'yellow' : 'green'
  const rotulo = inativa
    ? 'pausada'
    : f.atrasada
      ? 'sem sinal'
      : f.nunca_sinalizou
        ? 'aguardando 1º sinal'
        : 'em dia'

  return (
    <Stack gap={2}>
      <Group gap="xs">
        {/* `filled` + `autoContrast`: com `light` o rótulo media 2.02:1 no
            "aguardando" e 2.75:1 no "em dia", e trocar o tom não resolve — o que
            reprovava era texto BRANCO sobre amarelo e verde. E o rótulo do badge É o
            veredito, não decoração. */}
        <Badge color={cor} variant="filled" autoContrast>
          {rotulo}
        </Badge>
      </Group>
      <Text size="sm" style={{ color: 'var(--ai-text-primary, #1a2e35)' }}>
        {f.nunca_sinalizou
          ? `Nenhum sinal desde o cadastro (${formatTimeAgo(f.created_at)})`
          : `Último sinal ${formatTimeAgo(f.last_signal_at)}`}
        {/* Estava só no tooltip, e tooltip não existe no toque: quem abre a tela no
            celular é justamente quem está de plantão. */}
        {f.atrasada && !inativa && f.last_alert_at
          ? ` · registro aberto ${formatTimeAgo(f.last_alert_at)}`
          : ''}
      </Text>
    </Stack>
  )
}

function HeartbeatSourcesAdminInner() {
  const { data: fontes, isLoading } = useHeartbeatSources()
  const m = useHeartbeatSourceMutations()

  const [createOpen, createCtl] = useDisclosure(false)
  const [source, setSource] = useState('')
  const [stream, setStream] = useState('')
  const [janela, setJanela] = useState<number | string>(3600)

  const [editando, setEditando] = useState<HeartbeatSource | null>(null)
  const [janelaEdit, setJanelaEdit] = useState<number | string>(3600)

  const lista = fontes ?? []
  const atrasadas = lista.filter((f) => f.atrasada && f.is_active).length

  return (
    <Stack gap="md">
      <DataCard
        title="Fontes vigiadas por heartbeat"
        // `accent` e `badge` são a API real do DataCard (não há `subtitle`): quando há
        // fonte muda, o próprio cartão precisa dizer isso antes de alguém ler a tabela.
        accent={atrasadas > 0 ? 'danger' : 'primary'}
        badge={atrasadas > 0 ? { value: atrasadas, color: 'red' } : undefined}
        loading={isLoading}
        extra={
          <Button leftSection={<IconPlus size={16} />} onClick={createCtl.open}>
            Registrar fonte
          </Button>
        }
      >
        {lista.length > 0 && (
          <Text size="sm" mb="sm" style={{ color: 'var(--ai-text-secondary, #5f7882)' }}>
            {atrasadas > 0
              ? `${atrasadas} de ${lista.length} sem sinal dentro da janela declarada — cada uma dessas já abriu registro no canal do operador.`
              : 'Cada fonte declara de quanto em quanto tempo deve dar sinal. Silêncio além da janela abre registro sozinho, sem ninguém publicar nada.'}
          </Text>
        )}
        {lista.length === 0 ? (
          <EmptyState
            bare
            icon={IconHeartbeat}
            title="Nenhuma fonte vigiada"
            description="O vigia roda a cada minuto e hoje não tem o que vigiar: enquanto nenhuma fonte declarar sua janela, silêncio de coleta continua indistinguível de silêncio saudável."
            size="md"
            primaryAction={{ label: 'Registrar a primeira fonte', onClick: createCtl.open }}
          />
        ) : (
          // `minWidth` porque em 360px a coluna de ação saía da viewport e ficava
          // inalcançável — e é a coluna que age. `striped`/`highlightOnHover` são
          // default do tema (DS §6.3), então não se repetem aqui.
          <Table.ScrollContainer minWidth={720}>
            <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Fonte</Table.Th>
                <Table.Th>Sinal</Table.Th>
                <Table.Th>Janela</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {lista.map((f) => (
                <Table.Tr key={f.id}>
                  <Table.Td>
                    <Text fw={500}>{f.source}</Text>
                    {f.stream && (
                      <Text size="sm" style={{ color: 'var(--ai-text-primary, #1a2e35)' }}>
                        {f.stream}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <CelulaSinal f={f} />
                  </Table.Td>
                  <Table.Td>{formatJanela(f.window_seconds)}</Table.Td>
                  <Table.Td>
                    <Group gap="xs" justify="flex-end">
                      {/* Botão com NOME, e não switch: o portão cego contra
                          `AlertTeamsAdmin` mostrou que lá toda ação da linha se
                          identifica ("Membro", "Cobertura"). Um switch verde não diz se
                          é estado ou ação — só o `aria-label` dizia, e `aria-label` não
                          se lê com o olho. De quebra sai do alvo de toque de 28px. */}
                      <Button
                        size="sm"
                        variant="light"
                        color={f.is_active ? 'gray' : 'teal'}
                        leftSection={
                          f.is_active ? <IconPlayerPause size={14} /> : <IconPlayerPlay size={14} />
                        }
                        loading={m.update.isPending}
                        onClick={() =>
                          m.update.mutate({ id: f.id, is_active: !f.is_active })
                        }
                      >
                        {f.is_active ? 'Pausar' : 'Retomar'}
                      </Button>
                      <Button
                        size="sm"
                        variant="light"
                        onClick={() => {
                          setEditando(f)
                          setJanelaEdit(f.window_seconds)
                        }}
                      >
                        Janela
                      </Button>
                      <ActionIcon
                        color="red"
                        variant="subtle"
                        size="lg"
                        aria-label={`Remover ${f.source} do registro`}
                        title="Remover do registro"
                        loading={m.remove.isPending}
                        onClick={() => m.remove.mutate(f.id)}
                      >
                        <IconTrash size={18} />
                      </ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          </Table.ScrollContainer>
        )}
      </DataCard>

      <Modal opened={createOpen} onClose={createCtl.close} title="Registrar fonte">
        <Stack gap="sm">
          <TextInput
            label="Fonte"
            description="O mesmo valor que chega em platform_events.source — por exemplo, wazuh."
            value={source}
            onChange={(e) => setSource(e.currentTarget.value)}
            placeholder="wazuh"
          />
          <TextInput
            label="Sinal (opcional)"
            description="Para vigiar mais de um sinal da mesma fonte. Em branco, vigia a fonte inteira."
            value={stream}
            onChange={(e) => setStream(e.currentTarget.value)}
            placeholder="manager"
          />
          <NumberInput
            label="Janela (segundos)"
            description="Sem sinal dentro dela, o registro é aberto sozinho. Mínimo de 60 s, porque o vigia verifica a cada minuto."
            min={JANELA_MINIMA}
            max={JANELA_MAXIMA}
            value={janela}
            onChange={setJanela}
          />
          <Button
            loading={m.create.isPending}
            disabled={!source.trim() || Number(janela) < JANELA_MINIMA}
            onClick={() =>
              m.create.mutate(
                {
                  source: source.trim(),
                  stream: stream.trim(),
                  window_seconds: Number(janela),
                },
                {
                  onSuccess: () => {
                    setSource('')
                    setStream('')
                    setJanela(3600)
                    createCtl.close()
                  },
                },
              )
            }
          >
            Registrar
          </Button>
        </Stack>
      </Modal>

      <Modal
        opened={editando !== null}
        onClose={() => setEditando(null)}
        title={editando ? `Janela de ${editando.source}` : 'Janela'}
      >
        <Stack gap="sm">
          <NumberInput
            label="Janela (segundos)"
            description="Alterar a janela não apaga o último sinal: uma fonte em silêncio continua em silêncio."
            min={JANELA_MINIMA}
            max={JANELA_MAXIMA}
            value={janelaEdit}
            onChange={setJanelaEdit}
          />
          <Button
            loading={m.update.isPending}
            disabled={Number(janelaEdit) < JANELA_MINIMA}
            onClick={() =>
              editando &&
              m.update.mutate(
                { id: editando.id, window_seconds: Number(janelaEdit) },
                { onSuccess: () => setEditando(null) },
              )
            }
          >
            Salvar
          </Button>
        </Stack>
      </Modal>
    </Stack>
  )
}
